import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TrendingUp,
  Users,
  Calendar,
  DollarSign,
  Dumbbell,
  RefreshCw,
} from "lucide-react";
import { API } from "@/App";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";

const COLORS = ["#1B365D", "#DA291C", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899"];

export default function AnalyticsModule({ businessId }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard"]);
  const { formatPrice, currencySymbol } = useCurrency();

  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (businessId) {
      fetchAnalytics();
    }
  }, [businessId, period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API}/businesses/${businessId}/analytics?period=${period}`,
        { credentials: "include" }
      );
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-grey">
        {t('dashboard:analytics.analyticsDataFailed')}
      </div>
    );
  }

  const { summary, charts } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-xl text-navy">{t('dashboard:analytics.analytics')}</h2>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">{t('common:thisWeek')}</SelectItem>
              <SelectItem value="month">{t('common:thisMonth')}</SelectItem>
              <SelectItem value="year">{t('dashboard:analytics.thisYear')}</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAnalytics} className="rounded-xl">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">{summary.members.total}</p>
                <p className="text-xs text-grey">{t('dashboard:analytics.totalMembers')}</p>
                {summary.members.new > 0 && (
                  <Badge className="bg-green-100 text-green-700 text-xs mt-1">
                    +{summary.members.new} {t('dashboard:analytics.newCount')}
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">
                  {formatPrice(summary.revenue.total)}
                </p>
                <p className="text-xs text-grey">{t('common:totalRevenue')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">{summary.reservations.total}</p>
                <p className="text-xs text-grey">{t('dashboard:analytics.reservation')}</p>
                <div className="flex gap-1 mt-1">
                  <Badge className="bg-green-100 text-green-700 text-xs">
                    {summary.reservations.completed} ✓
                  </Badge>
                  <Badge className="bg-red-100 text-red-700 text-xs">
                    {summary.reservations.cancelled} ✗
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                <Dumbbell className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">{summary.trainers.total}</p>
                <p className="text-xs text-grey">{t('common:trainer')}</p>
                {summary.trainers.avgRating > 0 && (
                  <Badge className="bg-yellow-100 text-yellow-700 text-xs mt-1">
                    ⭐ {summary.trainers.avgRating.toFixed(1)}
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              {t('dashboard:analytics.revenueTrend')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={charts.revenueByMonth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${currencySymbol}${v / 1000}k`} />
                  <Tooltip
                    formatter={(value) => [`${currencySymbol}${(value || 0).toLocaleString()}`, t('dashboard:analytics.revenue')]}
                    labelStyle={{ color: "#1B365D" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#1B365D"
                    fill="#1B365D"
                    fillOpacity={0.2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Reservations by Day */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {t('dashboard:analytics.reservationsByDay')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={(charts.reservationsByDay || []).map(item => ({
                  ...item,
                  day: {
                    'Pzt': t('common:monShort'), 'Sal': t('common:tueShort'), 'Çar': t('common:wedShort'),
                    'Per': t('common:thuShort'), 'Cum': t('common:friShort'), 'Cmt': t('common:satShort'), 'Paz': t('common:sunShort')
                  }[item.day] || item.day
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#DA291C" radius={[4, 4, 0, 0]} name={t('dashboard:analytics.reservation')} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Branch Distribution */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">{t('dashboard:analytics.disciplineDistribution')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {charts.branchDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={charts.branchDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {charts.branchDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-grey">
                  {t('dashboard:analytics.noData')}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Trainer Performance */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Dumbbell className="w-5 h-5" />
              {t('dashboard:analytics.trainerPerformance')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {charts.trainerPerformance.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={charts.trainerPerformance} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
                    <Tooltip />
                    <Bar dataKey="completed" fill="#10B981" name={t('dashboard:analytics.completedCount')} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="reservations" fill="#1B365D" name={t('dashboard:analytics.totalCount')} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-grey">
                  {t('dashboard:analytics.noTrainerData')}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Member Growth */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5" />
            {t('dashboard:analytics.memberGrowth')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={charts.revenueByMonth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="members"
                  stroke="#8B5CF6"
                  strokeWidth={2}
                  dot={{ fill: "#8B5CF6" }}
                  name={t('members:newMember')}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
